// Locale-aware accessors for the /[lang]/boat-rental/[city] landing pages'
// hand-written copy. Same approach as winePageCopy (wine-content.ts): read off
// the imported resource JSON directly. The per-city block exists because the
// city has to be declined in FR/RU ("à Cannes", "в Каннах"), which a
// `{{city}}` interpolation of the Latin name can't do.

import type { Locale } from '@todo/core/i18n/locales';
import en from '@/lib/i18n/resources/en.json';
import fr from '@/lib/i18n/resources/fr.json';
import ru from '@/lib/i18n/resources/ru.json';

export type BoatRentalCityCopy = {
  /** On-page <h1>. */
  title: string;
  /** <title> base — fitted to the SERP budget by seoTitle() in seo.ts. */
  seoTitle: string;
  /** "in Cannes" / "à Cannes" / "в Каннах" — interpolated as {{inCity}} into the shared strings. */
  inCity: string;
};

type BoatRentalResource = {
  boatRental: {
    cities: Record<string, BoatRentalCityCopy>;
    typePlural: Record<string, string>;
    typePages: Record<string, { title: string }>;
  };
};

const resources: Record<Locale, BoatRentalResource> = { en, fr, ru };

/** A city's boat-rental page copy, or undefined for a city without authored copy. */
export function boatRentalCityCopy(locale: Locale, cityId: string): BoatRentalCityCopy | undefined {
  return resources[locale].boatRental.cities[cityId];
}

/**
 * A /boat-rental/[city]/[type] page's <h1> ("Catamaran rental in Cannes"), for
 * links and breadcrumbs outside the page itself, where there's no `t()`.
 */
export function boatTypePageTitle(locale: Locale, type: string, cityId: string): string | undefined {
  const city = boatRentalCityCopy(locale, cityId);
  const template = resources[locale].boatRental.typePages[type]?.title;
  return city && template ? template.replace('{{inCity}}', city.inCity) : undefined;
}

/** Lower-case plural for a boat_type ("motorboats"), for mid-sentence lists; falls back to the raw value. */
export function boatTypePlural(locale: Locale, type: string): string {
  return resources[locale].boatRental.typePlural[type] ?? type;
}
